import { ProgressBar, type ProgressBarLabel } from '@trustoo/components/ProgressBar/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const PROGRESS_BAR_USAGE = `import { ProgressBar } from '@trustoo/components/ProgressBar/v2'

// No label
<ProgressBar value={40} aria-label="Upload progress" />

// Inline / stacked label
<ProgressBar value={40} label="right" />
<ProgressBar value={40} label="bottom" />

// Floating tooltip anchored to the fill end
<ProgressBar value={40} label="top-floating" />

// Custom bounds + formatting
<ProgressBar value={3} min={0} max={5} label="right" formatLabel={(p) => \`\${Math.round(p)}%\`} />`

export const ProgressBarShowcase: React.FC = () => (
    <Section
        title="ProgressBar (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Progress bar</span> set
                (ComponentSet <InlineCode>5:17692</InlineCode>). A determinate bar:{' '}
                <InlineCode>bg-background-bg-quaternary</InlineCode> track +{' '}
                <InlineCode>bg-background-bg-brand-solid</InlineCode> fill. The canonical
                design-system bar at <InlineCode>@trustoo/components/ProgressBar/v2</InlineCode>{' '}
                (net-new — the various legacy package-local <InlineCode>ProgressBar</InlineCode>s
                are unrelated).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Label placements</h3>
                <div className="flex flex-col gap-spacing-4xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(
                        [
                            'none',
                            'right',
                            'bottom',
                            'top-floating',
                            'bottom-floating',
                        ] as ProgressBarLabel[]
                    ).map((label) => (
                        <div key={label} className="space-y-spacing-xs">
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {label}
                            </span>
                            <ProgressBar value={40} label={label} aria-label={`Example ${label}`} />
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Progress values</h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {[0, 25, 50, 75, 100].map((value) => (
                        <ProgressBar
                            key={value}
                            value={value}
                            label="right"
                            aria-label={`${value} percent`}
                        />
                    ))}
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={PROGRESS_BAR_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>value</InlineCode> + <InlineCode>min</InlineCode> /{' '}
                    <InlineCode>max</InlineCode>, <InlineCode>label</InlineCode> (none · right ·
                    bottom · top-floating · bottom-floating), and{' '}
                    <InlineCode>formatLabel</InlineCode>. Renders a{' '}
                    <InlineCode>role=&quot;progressbar&quot;</InlineCode> with the value clamped and
                    exposed via <InlineCode>aria-valuenow</InlineCode>.
                </p>
            </div>
        </div>
    </Section>
)
